<template>
    <div class="registration info-box is-card">
        <div class="box-main">
            <div class="box-icon is-hidden-touch">
                <i class="fad fa-presentation has-text-primary fa-2x"></i>
            </div>
            <div class="box-body">
                <div class="card-title"><i class="fad fa-presentation has-text-primary is-hidden-desktop mr-2"></i>{{ conference.title }}</div>
                <div class="card-meta">
                    <span><i class="far fa-fw fa-calendar-week mr-1"></i>{{ confDates }}</span>
                    <span v-if="conference.location" class="ml-3"><i class="far fa-fw fa-location-dot mr-1"></i>{{ conference.location }}</span>
                </div>
                <registration-chips class="mt-2"
                                    :window="registration.window" :state="registration.state" :outstanding="registration.outstanding"
                                    :open-apps="openApps" :is-complete="!!registration.is_complete"
                                    :start-at="conference.start_at" :end-at="conference.end_at"/>
            </div>
        </div>

        <!-- Alerts on their own bordered line: purchase order still pending, or forms left open -->
        <div class="box-alert" v-if="registration.is_po_pending">
            <i class="fas fa-circle-info mr-2"></i>
            <template v-if="conference.is_allow_invoice">You opted to pay by purchase order. Your registration isn't complete until that process is finished &mdash; generate the P.O. request and follow its instructions, or contact the organizer.</template>
            <template v-else>You opted to pay by purchase order. Your registration isn't complete until that process is finished. Contact the organizer with any questions.</template>
        </div>
        <div class="box-alert" v-else-if="hasOpenForms">
            <i class="fas fa-circle-info mr-2"></i>You have applications or forms for this conference that still need to be completed.
        </div>

        <div class="box-actions">
            <div class="box-lead box-info" v-if="registration.is_complete && registration.completed_at"><i class="far fa-fw fa-check-circle mr-2"></i>Completed {{ registration.completed_at | moment('MMMM Do, YYYY') }}</div>
            <div class="box-lead box-info" v-else-if="registration.created_at"><i class="far fa-fw fa-clock mr-2"></i>Registered {{ registration.created_at | moment('MMMM Do, YYYY') }}</div>

            <div class="box-buttons">
                <template v-if="registration.is_po_pending">
                    <b-button v-if="conference.is_allow_invoice" tag="router-link" :to="{name: 'po-request', params: {registrationId: registration.id}}" type="is-primary" outlined size="is-small"><i class="far fa-file-invoice mr-2"></i>Generate P.O. Request</b-button>
                    <b-button v-if="organizerMailto" tag="a" :href="organizerMailto" type="is-primary" outlined size="is-small"><i class="far fa-envelope mr-2"></i>Contact Organizer</b-button>
                </template>
                <b-button v-if="hasOpenForms" tag="router-link" :to="{name: 'conf-applications', params: {registrationId: registration.id}}" type="is-primary" outlined size="is-small"><i class="fas fa-file-pen mr-2"></i>Applications &amp; Forms</b-button>
                <b-button tag="router-link" :to="{name: 'conference', params: {registrationId: registration.id}}" type="is-primary" size="is-small"><i class="fas fa-presentation mr-2"></i>View Conference</b-button>
            </div>
        </div>
    </div>
</template>

<script>
    import RegistrationChips from "./registration/RegistrationChips";
    import {formatDateRange} from "../utils/dates";

    /**
     * Conference Records card. Same anatomy as the dashboard tile
     * (global .info-box.is-card: icon column, body, footer CTA area) with
     * the full set of actions: purchase-order follow-up, open forms, and
     * the conference itself. Status comes from the API's derived fields.
     */
    export default {
        name: "Registration",
        components: {RegistrationChips},
        props: {
            registration: {
                required: true
            }
        },
        computed: {
            conference() {
                return this.registration.conferences || {};
            },
            confDates() {
                return formatDateRange(this.conference.start_at, this.conference.end_at);
            },
            openApps() {
                return (this.registration.outstanding && this.registration.outstanding.open_apps) || 0;
            },
            // Legacy flag (required apps not yet completed) or the newer
            // derived signals — either way there is something to do under
            // Applications & Forms
            hasOpenForms() {
                const o = this.registration.outstanding || {};
                return !!(this.registration.isIncompleteApps || o.log_unsigned || this.openApps);
            },
            organizerMailto() {
                const c = this.conference;
                if (c.is_allow_invoice && c.po_email) return 'mailto:' + c.po_email + '?subject=' + encodeURIComponent(c.title + '/Purchase Order');
                if (!c.is_allow_invoice && c.support_email) return 'mailto:' + c.support_email + '?subject=' + encodeURIComponent(c.title + '/Purchase Order ' + (this.registration.code || ''));
                return null;
            }
        }
    };
</script>

<style lang="scss" scoped>
    // Layout comes from the global .info-box.is-card (assets/scss/_cta.scss)
    .card-title {
        font-weight: 700;
        line-height: 1.3;
    }

    .card-meta {
        font-size: 0.85rem;
        color: #7a7a7a;
    }
</style>
